import React from 'react';
import { createRoot } from 'react-dom/client';
window.React = React;
window.ReactDOM = { createRoot };

// Phase B4: shared contexts. App root wraps the tree in providers below;
// existing consumers still read user/location/locations as props (no
// behavior change). Future Phase B extractions migrate consumers to
// `useContext(MonarchContexts.UserContext)` one at a time.
import { UserContext } from './contexts/UserContext.jsx';
import { LocationContext } from './contexts/LocationContext.jsx';
import { TenantContext } from './contexts/TenantContext.jsx';
window.MonarchContexts = { UserContext, LocationContext, TenantContext };

import { urlBase64ToUint8Array } from './lib/push.js';
import { api, clearFirebaseSession } from './lib/api.js';

const { useState, useEffect } = React;

// The screens App mounts directly. Everything else is reached through
// <Dashboard> (authenticated app) or <KioskApp> (location-locked kiosk).
import { ChangePasswordModal } from './components/ChangePasswordModal.jsx';
import { OnLeaveReturnView } from './components/OnLeaveReturnView.jsx';
import { KioskApp } from './components/KioskApp.jsx';
import { Dashboard } from './components/Dashboard.jsx';
import { LoginScreen } from './components/LoginScreen.jsx';
import { InstallBanner } from './components/InstallBanner.jsx';
import { PushOptInBanner } from './components/PushOptInBanner.jsx';
import { ScheduleAcceptModal } from './components/ScheduleAcceptModal.jsx';
import { StoreConfirmModal } from './components/StoreConfirmModal.jsx';
import { ToastHost } from './components/ToastHost.jsx';

// LocationSelector removed in Round 2: users now auto-route from
// user.location_ids; the always-on pill bar at the top of Dashboard
// handles switching for multi-location roles.

// ─── Real-Time Dashboard (Empire Tea Dashboard CLI spec §3) ──────────────────
// Backed by GET /api/realtime/:locationKey. Auto-refreshes every 5 min per
// the spec. v1 renders header + scorecard + hourly chart + decision-guide
// phase strip. Labor / On-Clock / Schedule Gantt / Watch List arrive in
// later slices; the backend currently stubs those fields so this component
// already does the right thing when they wire up (it just renders nothing).

// ─── Schedule section (sidebar Schedule + sub-tabs) ──────────────────────────

// ─── Weekly Schedule view (Schedule sidebar → Week view) ─────────────────────
// Seven daily Gantts on one page (Mon → Sun) with the schedule rendered
// as the bar and the actual time-clock punches drawn as a contrasting
// line through the same row. Per-row badges flag time-clock issues
// >±5 min (late, early-in, overtime, early-out) and missed clocks.
// Week navigation: ← / picker / → / This week. Window guarded by the
// server (-31 → +28 days).

// ─── My Schedule wrapper (My / Grid / Gantt view toggle) ─────────────────────
// Default sub-tab inside Schedule. Three view modes share the same
// Square-Labor week payload:
//   - my   : just the current user's shifts (compact week-strip)
//   - grid : full Mon-Sun × scheduled-staff matrix (no empty rows)
//   - gantt: the existing 7-DayPanel view (WeekScheduleView)

// ─── Shift swaps ────────────────────────────────────────────────────────────
// One surface that shows the manager review queue (if applicable),
// my outgoing offers, my direct incoming offers, and any open offers
// at locations I work at.

// Shared push-enable helper. Used by both the in-profile toggle and the
// app-level opt-in banner. Resolves true on success, false on user
// rejection / failure. Throws on programmer errors (no VAPID, no SW).
async function enableWebPush(user) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('Your browser does not support push notifications.');
  }
  const headers = { 'x-user-id': String(user.id) };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.register('/service-worker.js');
  await navigator.serviceWorker.ready;
  const keyResp = await api('GET', '/api/push/public-key', null, headers);
  if (!keyResp?.publicKey) throw new Error('Push not configured on the server.');
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(keyResp.publicKey),
  });
  const json = sub.toJSON();
  await api('POST', '/api/push/subscribe',
    { endpoint: json.endpoint, keys: json.keys }, headers);
  return true;
}

// Top-level routing: /kiosk/<location-key> mounts the location-locked
// KioskApp; every other path mounts the regular App.
const KIOSK_PATH_RE = /^\/kiosk\/([a-z_]+)\/?$/i;

function App() {
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [storeConfirmed, setStoreConfirmed] = useState(false);   // multi-store staff picked today

  // Locations is an authed call (umbrella requireAuth, post-Phase-2). Don't
  // fire it before the user is set — pre-login the Bearer token isn't in
  // localStorage yet, so the request 401s and the uncaught rejection in this
  // useEffect prevents setLocations from ever running. With empty deps the
  // effect never re-fires post-login, so locations stays [], the auto-route
  // effect skips on locations.length === 0, and App renders null → white
  // screen. Hence: gate on user, plus a defensive .catch().
  useEffect(() => {
    if (!user) return;
    api('GET', '/api/locations').then(setLocations).catch(() => setLocations([]));
  }, [user]);

  // Auto-route into the dashboard as soon as we have both the user and the
  // locations list. Owners/admins/managers see all locations; staff see only
  // the locations they're assigned to. No intermediate selector screen.
  //
  // CJ 2026-05-21: default to the location they're actively working at (or
  // next scheduled at), so a staff member at JT Connell doesn't open the
  // app and see Bristol's task list. Order of preference (computed by
  // /api/me/timeclock/status as preferred_location_id):
  //   open punch > today's shift > next future shift > primary_location_id
  useEffect(() => {
    if (!user || !Array.isArray(locations) || locations.length === 0) return;
    if (location) return;
    let assigned = locations;
    if (!window.EmpireRoles.hasCapability(user, 'view_all_locations')) {
      let assignedIds = [];
      if (user.location_ids) {
        try { assignedIds = JSON.parse(user.location_ids); } catch {}
      } else if (user.location_id) {
        assignedIds = [user.location_id];
      }
      if (assignedIds.length > 0) {
        assigned = locations.filter((l) => assignedIds.includes(l.id));
      }
    }
    if (assigned.length === 0) assigned = locations;
    setSelectedLocations(assigned);
    // Hit timeclock status for the preferred-location guess; fall back to
    // primary or first-assigned if the call fails or returns nothing usable.
    api('GET', '/api/me/timeclock/status', null, { 'x-user-id': String(user.id) })
      .then(s => {
        const preferred = s?.preferred_location_id
          ? assigned.find(l => l.id === s.preferred_location_id)
          : null;
        const primary = user.primary_location_id
          ? assigned.find((l) => l.id === user.primary_location_id)
          : null;
        setLocation(preferred || primary || assigned[0]);
      })
      .catch(() => {
        const primary = user.primary_location_id
          ? assigned.find((l) => l.id === user.primary_location_id)
          : null;
        setLocation(primary || assigned[0]);
      });
  }, [user, locations]);

  // Store confirmation at login (CJ 2026-07-31). With Monarch's clock retired,
  // task / waste / shift-meal entries need to know which store you're at — a
  // staff member assigned to 2+ cafés confirms it once per day so those entries
  // attribute correctly. Single-store staff + managers (who have the switcher)
  // never see it. Remembered per user per day in localStorage.
  const assignedStores = React.useMemo(() => {
    if (!user || !Array.isArray(locations) || locations.length === 0) return [];
    if (window.EmpireRoles.hasCapability(user, 'view_all_locations')) return locations;
    let ids = [];
    try { ids = user.location_ids ? JSON.parse(user.location_ids) : (user.location_id ? [user.location_id] : []); } catch { ids = []; }
    const mine = locations.filter((l) => ids.includes(l.id));
    return mine.length ? mine : locations;
  }, [user, locations]);
  const storeConfirmKey = user ? `storeConfirm_${user.id}_${new Date().toLocaleDateString('en-CA')}` : null;
  useEffect(() => {
    if (!user || !storeConfirmKey || assignedStores.length < 2) return;
    const saved = Number(window.localStorage.getItem(storeConfirmKey) || 0);
    const match = saved && assignedStores.find((l) => l.id === saved);
    if (match) { setLocation(match); setStoreConfirmed(true); }
  }, [user, assignedStores, storeConfirmKey]);
  function confirmStore(store) {
    setLocation(store);
    try { window.localStorage.setItem(storeConfirmKey, String(store.id)); } catch { /* private mode */ }
    setStoreConfirmed(true);
  }
  const needsStoreConfirm = !!user && assignedStores.length >= 2 && !storeConfirmed
    && !window.EmpireRoles.hasCapability(user, 'view_all_locations');

  // CJ 2026-05-21: push opt-in banner state. We surface a persistent
  // banner when the browser supports push but the user isn't subscribed
  // (or has explicitly denied). Reason: the schedule-accept workflow +
  // chat depend on push. Banner dismissal stashes a timestamp in
  // localStorage so we don't pester for 7 days.
  const [pushStatus, setPushStatus] = useState('checking'); // 'checking' | 'unsupported' | 'subscribed' | 'denied' | 'unsubscribed' | 'dismissed'
  useEffect(() => {
    if (!user) return;
    (async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        setPushStatus('unsupported');
        return;
      }
      const dismissedAt = Number(localStorage.getItem('monarchPushBannerDismissedAt') || 0);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Notification.permission === 'granted') {
        try {
          const reg = await navigator.serviceWorker.getRegistration();
          const sub = reg ? await reg.pushManager.getSubscription() : null;
          setPushStatus(sub ? 'subscribed' : 'unsubscribed');
        } catch (_) { setPushStatus('unsubscribed'); }
      } else if (Notification.permission === 'denied') {
        setPushStatus(Date.now() - dismissedAt < sevenDays ? 'dismissed' : 'denied');
      } else {
        setPushStatus(Date.now() - dismissedAt < sevenDays ? 'dismissed' : 'unsubscribed');
      }
    })();
  }, [user?.id]);
  async function enablePushFromBanner() {
    try {
      const ok = await enableWebPush(user);
      if (ok) setPushStatus('subscribed');
      else setPushStatus('denied');
    } catch (e) { alert(e.message || 'Could not enable notifications.'); }
  }
  function dismissPushBanner() {
    try { localStorage.setItem('monarchPushBannerDismissedAt', String(Date.now())); } catch (_) {}
    setPushStatus('dismissed');
  }

  // CJ 2026-05-21: schedule acceptance modal. On login, fetch unaccepted
  // upcoming schedules and pop a modal listing the shifts with an Accept
  // button. Re-checks each time the user changes.
  const [pendingAcceptances, setPendingAcceptances] = useState(null);
  useEffect(() => {
    if (!user) { setPendingAcceptances(null); return; }
    api('GET', '/api/me/schedule/pending-acceptances', null, { 'x-user-id': String(user.id) })
      .then(r => setPendingAcceptances(Array.isArray(r?.pending) ? r.pending : []))
      .catch(() => setPendingAcceptances([]));
  }, [user?.id]);
  async function acceptSchedule(locationId, weekStart) {
    try {
      await api('POST', '/api/me/schedule/accept',
        { location_id: locationId, week_start: weekStart, method: 'modal' },
        { 'x-user-id': String(user.id) });
      setPendingAcceptances(p => (p || []).filter(x =>
        !(x.location_id === locationId && x.week_start === weekStart)
      ));
    } catch (e) { alert(e.message || 'Accept failed'); }
  }

  function handleLogout() {
    clearFirebaseSession();
    setUser(null); setLocation(null); setSelectedLocations([]); setStoreConfirmed(false);
  }

  if (!user) return <><LoginScreen onLogin={setUser} /><InstallBanner /></>;
  // Forced password change (newly created accounts still on the generated
  // temp password). Block all UI behind this modal until the user picks a
  // new password. Continue → onClose flips the flag locally.
  if (user.password_change_required) {
    return (
      <ChangePasswordModal
        user={user}
        forced
        onClose={() => setUser(u => ({ ...u, password_change_required: false }))} />
    );
  }
  // On leave: one screen, nothing else (CJ 2026-08-04, D229). Deliberately
  // ahead of the location gate — an on-leave person may have no resolvable
  // location, and returning null there would show them a blank page. This is
  // presentation only; the server still resolves active-only for every other
  // route, so it is not the security boundary.
  if (user.status === 'on_leave') {
    return <OnLeaveReturnView user={user} onSignOut={handleLogout} />;
  }
  if (!location) return null;
  // Multi-store staff confirm their store for the day before the app opens.
  if (needsStoreConfirm) {
    return <StoreConfirmModal user={user} stores={assignedStores} onConfirm={confirmStore} />;
  }
  // Phase B4: providers wrap the existing tree. Existing components keep
  // reading user/location from props (no migration in this commit) —
  // the providers just expose the same values via context for future
  // Phase B consumers to pick up one at a time.
  return (
    <UserContext.Provider value={user}>
      <LocationContext.Provider value={{ active: location, override: null }}>
        <TenantContext.Provider value={{ id: 1, settings: null }}>
          <Dashboard user={user} location={location} locations={locations}
            selectedLocations={selectedLocations} onSwitchLocation={setLocation}
            onBack={handleLogout} />
          <InstallBanner />
          {(pushStatus === 'unsubscribed' || pushStatus === 'denied') && (
            <PushOptInBanner status={pushStatus}
              onEnable={enablePushFromBanner} onDismiss={dismissPushBanner} />
          )}
          {pendingAcceptances && pendingAcceptances.length > 0 && (
            <ScheduleAcceptModal pending={pendingAcceptances} onAccept={acceptSchedule} />
          )}
        </TenantContext.Provider>
      </LocationContext.Provider>
    </UserContext.Provider>
  );
}

// Top-level routing: if the URL is /kiosk/<location-key>, mount the
// location-locked KioskApp; everything else mounts the regular App.
function Root() {
  const path = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
  const kioskMatch = path.match(KIOSK_PATH_RE);
  if (kioskMatch) return <KioskApp locationKey={kioskMatch[1].toLowerCase()} />;
  return <App />;
}

// Phase C3: root-level Error Boundary. Catches render-time errors anywhere
// in the tree so a single bad component shows a recoverable fallback
// instead of a white screen. React only supports Error Boundaries via
// class components (per the React docs) — there's no hooks equivalent.
// Logs to console for whatever monitoring is in place; in prod the
// browser console hits Cloud Logging's BetterStack tail via the
// existing window.onerror handler.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidCatch(err, info) {
    console.error('[ErrorBoundary] render error:', err, info?.componentStack);
  }
  render() {
    if (!this.state.err) return this.props.children;
    const msg = this.state.err?.message || String(this.state.err);
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', background: '#f6f4ee', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', color: '#1c1f1d' }}>
        <div style={{ maxWidth: '420px', background: '#ffffff', borderRadius: '16px', borderTop: '4px solid #b48a47', boxShadow: '0 6px 28px rgba(28,31,29,0.08)', padding: '32px' }}>
          <div style={{ fontSize: '12px', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#b48a47', fontWeight: 600, marginBottom: '12px' }}>Something went wrong</div>
          <div style={{ fontSize: '17px', lineHeight: 1.5, marginBottom: '14px' }}>The app hit an error and couldn't keep rendering. Refreshing usually fixes it.</div>
          <div style={{ fontSize: '12px', color: '#7a7a7a', wordBreak: 'break-word', marginBottom: '18px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{msg}</div>
          <button onClick={() => window.location.reload()} style={{ background: '#b48a47', color: '#ffffff', border: 0, borderRadius: '8px', padding: '11px 18px', fontWeight: 600, fontSize: '14px', cursor: 'pointer' }}>Reload</button>
        </div>
      </div>
    );
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <ErrorBoundary><Root /><ToastHost /></ErrorBoundary>
);
